'use memo'

import React from 'react'
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { EditPlayerFormValues } from '@sportninja/common/features/profile/player'
import { isFieldSectionKey } from '@sportninja/common/features/profile/player/playerDetails/utils/sectionFields'
import { isProfileExtraSectionShown } from '@sportninja/common/features/profile/player/playerDetails/utils/playerProfileExtras'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import useWindowSize from 'src/hooks/useWindowSize'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import { FullScreenModalHeader } from 'src/components/FullScreenModal'
import SalesforceIdChip from 'src/features/team/player/components/SalesforceIdChip'
import { useEditPlayerContext } from './EditPlayerContext'
import { useSectionEdit } from './context/SectionEditContext'
import { useGuardedClose } from './hooks/useGuardedClose'
import { useModalBreadcrumbs } from './hooks/useModalBreadcrumbs'
import type { ModalTrailItem } from './types/modalTrail'
import FamilyMembersSection from 'src/features/profile/family/components/FamilyMembersSection'
import { PLAYER_SETUP_ITEMS } from './playerSetupItems'
import HeaderCancelIcon from './components/HeaderCancelIcon'
import PlayerFormSkeleton from './components/PlayerFormSkeleton'
import PersonalSection from './components/sections/PersonalSection'
import AddressSection from './components/sections/AddressSection'
import EmergencyContactSection from './components/sections/EmergencyContactSection'
import SocialProfilesSection from './components/sections/SocialProfilesSection'
import MembershipSection from './components/sections/MembershipSection'
import ProfileExtraSection from './components/sections/ProfileExtraSection'
import ConfirmDiscardChangesModal from './components/ConfirmDiscardChangesModal'

interface EditPlayerModalContentProps {
  trail?: ModalTrailItem[]
  /** "Back to Profile" — a back link in place of the breadcrumb. */
  backLabel?: string
}

const EditPlayerModalContent: React.FC<EditPlayerModalContentProps> = ({
  trail,
  backLabel,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768

  const { isFetchLoading, playerName, onCancel, salesforceId, playerId } =
    useEditPlayerContext()
  const sectionEdit = useSectionEdit()
  const { dirty } = useFormikContext<EditPlayerFormValues>()

  // Close guard: warn before discarding edits, but close straight away when
  // the form is untouched. In read mode only an open section can hold edits.
  const hasUnsavedEdits = sectionEdit
    ? Boolean(sectionEdit.editingSection) && dirty
    : dirty
  const { requestClose, isConfirmOpen, confirmDiscard, cancelDiscard } =
    useGuardedClose({ hasUnsavedEdits, onClose: onCancel })
  const breadcrumbs = useModalBreadcrumbs(trail, requestClose)

  // Family Members needs a saved player to attach contacts to.
  // A player on their own profile doesn't get the admin-only sections
  // (Notes), so their sidebar entries go too.
  const setupItems = PLAYER_SETUP_ITEMS.filter(
    (item) =>
      (item.key !== 'family' || Boolean(playerId)) &&
      (!isFieldSectionKey(item.key) ||
        isProfileExtraSectionShown(item.key, 'self'))
  )

  return (
    <>
      {/* Player Details' header: where the user is over the player's name, and
          Close — each section saves on its own. */}
      <FullScreenModalHeader
        breadcrumbs={breadcrumbs.items}
        onHome={breadcrumbs.onHome}
        // Runs the same guard as Close (the discard warning first).
        onBack={backLabel ? () => requestClose() : undefined}
        backLabel={backLabel}
        // A "Back to Profile" link already says where this is.
        caption={
          playerName && !backLabel
            ? capitalizeWords('Player Details')
            : undefined
        }
        title={playerName || capitalizeWords('Player Details')}
        actions={
          <HeaderActionButton
            text={capitalizeWords('Close')}
            onClick={() => requestClose()}
            variant='secondary'
            textWeight='medium'
            disabled={Boolean(sectionEdit?.isSaving)}
            icon={<HeaderCancelIcon />}
          />
        }
      />

      {isFetchLoading ? (
        <PlayerFormSkeleton stacked={isMobile} />
      ) : (
        <ScrollSpyLayout items={setupItems} stacked={isMobile} gap={24}>
          <ScrollSpyLayout.Sidebar
            width={256}
            ariaLabel='Player form categories'
          />
          <ScrollSpyLayout.Content
            ariaLabel='Player profile sections'
            paddingRight={16}
          >
            {/* Email and name are locked here — this modal only ever edits an
                existing player, and they identify the player. */}
            <PersonalSection
              // Display-only `*` markers — validation stays the API's job.
              requiredNameFields
              requireEmail
              lockEmail
              lockNames
              // Canlan-only; renders null when the profile has no sf_id.
              extraIdChips={<SalesforceIdChip salesforceId={salesforceId} />}
            />
            <AddressSection />
            <EmergencyContactSection />
            {playerId ? <FamilyMembersSection playerId={playerId} /> : null}
            {/* A player on their own profile: no admin-only fields. */}
            <ProfileExtraSection sectionKey='experience' audience='self' />
            <ProfileExtraSection sectionKey='preferences' audience='self' />
            <ProfileExtraSection sectionKey='additional' audience='self' />
            <SocialProfilesSection />
            <MembershipSection />
          </ScrollSpyLayout.Content>
        </ScrollSpyLayout>
      )}

      <ConfirmDiscardChangesModal
        open={isConfirmOpen}
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </>
  )
}

export default EditPlayerModalContent
